import { useState } from "react"
import { useLocation } from "react-router-dom"
import { MessageSquareWarning, Loader2 } from "lucide-react"
import html2canvas from "html2canvas-pro"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { Checkbox } from "@/components/ui/checkbox"
import { useAuth } from "@/hooks/useAuth"
import { submitGenericFeedback } from "@/services/feedbackService"

/**
 * FeedbackDialog renders a chat-icon button that opens a dialog for user feedback.
 * On submit, it sends the feedback message along with the user's identity and the
 * current page path to the generic feedback API endpoint. Optionally captures a
 * screenshot of the current page if the user checks the "Include a screenshot" option.
 */
export function FeedbackDialog(): React.JSX.Element {
  const [feedback, setFeedback] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [includeScreenshot, setIncludeScreenshot] = useState<boolean>(false)

  const { user, getAccessToken } = useAuth()
  const location = useLocation()

  /**
   * Captures a screenshot of the current page using html2canvas.
   * The dialog portal is removed from the cloned DOM inside onclone so it
   * never appears in the rendered output. WebGL canvases (deck.gl / maplibre)
   * are manually composited in the same callback to ensure their pixel data
   * is captured correctly.
   *
   * @returns A base64-encoded JPEG string, or undefined if capture fails.
   */
  async function captureScreenshot(): Promise<string | undefined> {
    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        scale: 1,
        ignoreElements: (element: Element) => {
          // Skip the dialog overlay (backdrop) and the dialog content itself.
          // These are siblings inside the portal, so both need to be excluded.
          return (
            element.getAttribute("data-slot") === "dialog-overlay" ||
            element.getAttribute("role") === "dialog"
          )
        },
        onclone: (clonedDoc: Document) => {
          // html2canvas cannot natively read WebGL canvas content.
          // We grab the pixel data from the live canvases and draw it
          // onto the cloned 2D canvases so they appear in the output.
          const liveCanvases = document.querySelectorAll<HTMLCanvasElement>("canvas")
          const clonedCanvases = clonedDoc.querySelectorAll<HTMLCanvasElement>("canvas")

          liveCanvases.forEach((liveCanvas, index) => {
            const clonedCanvas = clonedCanvases[index]
            if (!clonedCanvas) return

            try {
              const ctx = clonedCanvas.getContext("2d")
              if (ctx) {
                clonedCanvas.width = liveCanvas.width
                clonedCanvas.height = liveCanvas.height
                ctx.drawImage(liveCanvas, 0, 0)
              }
            } catch {
              // Security or tainted canvas errors — skip silently
            }
          })
        },
      })

      // Convert canvas to base64 JPEG at 0.7 quality for compression
      const dataUrl = canvas.toDataURL("image/jpeg", 0.7)
      const base64 = dataUrl.replace(/^data:image\/jpeg;base64,/, "")
      return base64
    } catch (err) {
      console.error("Failed to capture screenshot:", err)
      return undefined
    }
  }

  /**
   * Handles the feedback form submission.
   * Collects the feedback text, current user name, page path, and optionally
   * a screenshot of the page, then sends it to the generic feedback API.
   */
  async function handleSubmit(): Promise<void> {
    setIsSubmitting(true)
    setError(null)

    try {
      const idToken = await getAccessToken()
      const userName = user?.name ?? user?.email ?? "Unknown User"

      // Capture screenshot if the user opted in
      let screenshot: string | undefined
      if (includeScreenshot) {
        screenshot = await captureScreenshot()
      }

      await submitGenericFeedback(
        {
          message: feedback,
          userName: userName,
          pagePath: location.pathname,
          screenshot: screenshot,
        },
        idToken
      )

      // Reset and close on success
      setFeedback("")
      setIncludeScreenshot(false)
      setIsOpen(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Something went wrong"
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (open) setError(null) }}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DialogTrigger asChild>
            <Button variant="ghost" size="icon" className="!h-[32px]" aria-label="Send feedback">
              <MessageSquareWarning className="size-[1.2rem]" />
              <span className="sr-only">Send feedback</span>
            </Button>
          </DialogTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>Send feedback</p>
        </TooltipContent>
      </Tooltip>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send Feedback</DialogTitle>
          <DialogDescription>
            We'd love to hear your thoughts. Please let us know any issues encountered,
            improvements that could be made, or feature requests you may have.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          placeholder="Type your feedback here..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          className="h-[150px] resize-none overflow-y-auto"
          aria-label="Feedback message"
          disabled={isSubmitting}
        />
        <div className="flex items-center gap-2">
          <Checkbox
            id="include-screenshot"
            checked={includeScreenshot}
            onCheckedChange={(checked) => setIncludeScreenshot(checked === true)}
            disabled={isSubmitting}
            aria-label="Include a screenshot of this page"
          />
          <label
            htmlFor="include-screenshot"
            className="text-sm text-muted-foreground cursor-pointer select-none"
          >
            Include a screenshot of this page
          </label>
        </div>
        <DialogFooter>
          {error && (
            <p className="text-sm text-destructive mr-auto self-center" role="alert">
              {error}
            </p>
          )}
          <Button
            onClick={handleSubmit}
            disabled={feedback.trim().length === 0 || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="size-4 animate-spin mr-2" />
                Submitting…
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
